import { FetcherMultiErrorResponse } from '@/types/types';
import { useFetcherWithUtils } from '@/hooks/useFetcherWithUtils';
import { OutlinedButton } from '@/components/layouts/Buttons/OutlinedButton/OutlinedButton';
import s from './WorkspaceSettings.module.scss';
import { WorkspaceFromStore } from '@/types/workspaces';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { assert } from '@/utils/asserts';
import { EntityUserManagement } from '@/components/Views/EntityUserManagement';
import { useGetPermissionsLevels } from '@/utils/helpers';
import { PermissionType } from '@/types/auth';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { useRouteLoaderData } from 'react-router-dom';
import { useOrganizationStore } from '@/stores/organizationStore';
import { DeleteEntity } from '@/components/Settings/DeleteEntity/DeleteEntity';
import { EntitySettingsInfo } from '@/components/Settings/EntitySettingsInfo/EntitySettingsInfo';
import { WorkspacesService } from '@/services/workspacesService';
import { useWorkspaceInvitations } from '@/services/queries/useWorkspaceInvitations';
import { useWorkspacePermissions } from '@/services/queries/useWorkspacePermissions';

export function WorkspaceSettingsView() {
  const workspaceId = useWorkspaceIdFromParams();
  const [fetcher, isLoading] = useFetcherWithUtils<FetcherMultiErrorResponse>();
  const [workspaceFetcher, workspaceIsLoading] =
    useFetcherWithUtils<FetcherMultiErrorResponse>();
  const [, , isAdmin] = useGetPermissionsLevels(PermissionType.Workspace);
  const userId = useUserPermissionsStore((state) => state.userId) as NonNullable<string>;
  const { workspaces } = useRouteLoaderData('workspaces') as {
    workspaces: WorkspaceFromStore[];
  };
  const selectedOrganization = useOrganizationStore(
    (state) => state.selectedOrganization,
  );

  assert(workspaceId, 'workspaceId is required');

  const invitations = useWorkspaceInvitations(workspaceId);
  const permissions = useWorkspacePermissions(workspaceId);

  const handleLeaveWorkspace = () => {
    const formData = new FormData();
    formData.append('type', 'permission');
    formData.append('permissionId', userId);
    fetcher.submit(formData, { method: 'delete' });
  };

  const handleDeleteWorkspace = () => {
    const formData = new FormData();
    formData.append('type', 'workspace');
    formData.append('workspaceId', workspaceId);
    workspaceFetcher.submit(formData, { method: 'delete' });
  };

  const currentWorkspace = workspaces.find((workspace) => workspace.id === workspaceId);
  return (
    <>
      <EntitySettingsInfo
        isAdmin={isAdmin}
        isOrganization={false}
        entityId={workspaceId}
        entityType="workspaces"
        entityName={currentWorkspace?.name as string}
        showUserOptions={isAdmin || !selectedOrganization?.isOrganizationMember}
      >
        <EntityUserManagement
          entityId={workspaceId}
          changeRole={WorkspacesService.changeRole}
          changeInvitationRole={WorkspacesService.changeInvitationRole}
          deleteInvitation={WorkspacesService.deleteInvitation}
          deletePermission={WorkspacesService.deletePermission}
          isAdmin={isAdmin}
          invitations={invitations}
          permissions={permissions}
        />
      </EntitySettingsInfo>

      <hr style={{ width: '100%', marginTop: '1rem' }} />

      <OutlinedButton
        isLoading={isLoading}
        onClick={handleLeaveWorkspace}
        className={s.leaveButton}
        spinnerSize="20px"
        spinnerColor="red"
      >
        Leave Workspace
      </OutlinedButton>

      <DeleteEntity
        deleteEntity={handleDeleteWorkspace}
        entityType="workspaces"
        isLoading={workspaceIsLoading}
      />
    </>
  );
}
